// Seeded PRNG (IMPLEMENTATION §0.1, §2.1): mulberry32 on a uint32 state that lives in SimState, so saves,
// replays and the Unity port continue the exact stream. Integer-only: Math.imul and `>>> 0` wrap like C#
// `unchecked` uint arithmetic; no float is produced (the reference returns out / 2^32, we return out).
//
// C# port:
//   uint a = state + 0x6D2B79F5u;  uint t = (a ^ (a >> 15)) * (1u | a);
//   t = (t + ((t ^ (t >> 7)) * (61u | t))) ^ t;  out = t ^ (t >> 14);
import type { Int } from './types.ts';

/** 2^32 as an integer. */
export const U32 = 4294967296;
/** Largest n for randBelow: u32·n stays below 2^53, so the multiply-shift is exact in JS doubles. */
export const RAND_BELOW_MAX = 2097152;

/** Advances a mulberry32 state (uint32 → uint32). */
export function mb32Next(a: Int): Int {
  return (a + 0x6d2b79f5) >>> 0;
}

/** The output (uint32) of an ALREADY ADVANCED state. */
export function mb32Out(a: Int): Int {
  let t = Math.imul(a ^ (a >>> 15), 1 | a);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return (t ^ (t >>> 14)) >>> 0;
}

/** Any integer seed → a uint32 PRNG state. */
export function seedState(seed: Int): Int {
  return seed >>> 0;
}

/** Draws the next uint32 from `h.rng` and advances it. */
export function nextU32(h: { rng: Int }): Int {
  h.rng = mb32Next(h.rng);
  return mb32Out(h.rng);
}

/**
 * Maps a uint32 to [0, n) by multiply-shift: floor(u·n / 2^32). C#: (int)(((ulong)u * (ulong)n) >> 32).
 * Exact for n ≤ RAND_BELOW_MAX.
 */
export function randBelow(u: Int, n: Int): Int {
  if (!(n > 0 && n <= RAND_BELOW_MAX)) throw new RangeError(`randBelow: n=${n} out of range`);
  return Math.floor((u * n) / U32);
}
